import { supabase } from "@/supabase/client";

export const checkRewardClaimed = async (uid: string, missionId: string, checkDaily = false) => {
  try {
    // If checking daily claims, add date filters
    if (checkDaily) {
      // Get today's start and end timestamps in UTC
      const now = new Date();
      const startOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
      const endOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));
      
      const { data, error } = await supabase
        .from('sys_rewards')
        .select('*')
        .eq('uid', uid)
        .eq('mission_id', missionId)
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString());
        
      if (error) {
        console.error("Error checking daily reward claims:", error);
        return false;
      }
      
      return data && data.length > 0;
    } else {
      // Original functionality - check if ever claimed
      const { data, error } = await supabase
        .from('sys_rewards')
        .select('*')
        .eq('uid', uid)
        .eq('mission_id', missionId);
      
      if (error) {
        console.error("Error checking reward claims:", error);
        return false;
      }
      
      return data && data.length > 0;
    }
  } catch (error) {
    console.error("Error checking reward claims:", error);
    return false;
  }
};

interface Mission {
  mission_id: string;
  end_time_unix?: number;
  end_time?: string;
  start_time_unix?: number;
  start_time?: string;
  frequency: string;
  is_test?: boolean;
  is_enabled: boolean;
  is_visible: boolean;
  category: string;
  [key: string]: any;
}

export const fetchAllMissions = async (): Promise<Mission[]> => {
  try {
    const { data: missions, error } = await supabase
      .from('missions')
      .select('*')
      .eq('is_enabled', true)
      .eq('is_visible', true)
      .eq('category', 'EXTERNAL');

    if (error) {
      console.error("Error fetching enabled missions:", error);
      return [];
    }

    // Transform the data to match the expected format
    const transformedMissions = missions.map(mission => {
      // Convert end_time_unix to seconds format if it exists
      const endTimeSeconds = mission.end_time_unix 
        ? { seconds: mission.end_time_unix }
        : mission.end_time 
          ? { seconds: Math.floor(new Date(mission.end_time).getTime() / 1000) }
          : null;

      // Convert start_time_unix to seconds format if it exists
      const startTimeSeconds = mission.start_time_unix
        ? { seconds: mission.start_time_unix }
        : mission.start_time
          ? { seconds: Math.floor(new Date(mission.start_time).getTime() / 1000) }
          : null;

      return {
        id: mission.mission_id,
        ...mission,
        end_time: endTimeSeconds,
        start_time: startTimeSeconds
      };
    });

    // Filter out missions that have ended (for non-daily missions)
    const currentTime = Math.floor(Date.now() / 1000);
    const filteredMissions = transformedMissions.filter(mission => {
      if (mission.frequency === "daily") {
        return true;
      }
      
      // For non-daily missions, check if they have ended
      if (mission.end_time?.seconds) {
        return currentTime <= mission.end_time.seconds;
      }
      
      return true;
    });

    return filteredMissions;
  } catch (error) {
    console.error("Error fetching enabled missions:", error);
    return [];
  }
};

export const hasClaimedInternalDailyReward = async (uid: string, game_name: string): Promise<boolean> => {
  try {
    // Get today's start and end timestamps in UTC
    const now = new Date();
    const startOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
    const endOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));

    // Get internal missions for this game
    const { data: internalMissions, error: missionsError } = await supabase
      .from('missions')
      .select('mission_id')
      .eq('game_name', game_name)
      .eq('category', 'INTERNAL')
      .eq('frequency', 'daily');

    if (missionsError) {
      console.error("Error fetching internal missions:", missionsError);
      return false;
    }

    if (!internalMissions || internalMissions.length === 0) {
      console.log(`No internal daily missions found for game ${game_name}`);
      return false;
    }

    // Get internal mission IDs
    const internalMissionIds = internalMissions.map(m => m.mission_id);

    // Check if any internal mission rewards have been claimed today
    const { data: rewards, error: rewardsError } = await supabase
      .from('sys_rewards')
      .select('*')
      .eq('uid', uid)
      .in('mission_id', internalMissionIds)
      .gte('created_at', startOfDay.toISOString())
      .lte('created_at', endOfDay.toISOString());

    if (rewardsError) {
      console.error("Error searching internal rewards:", rewardsError);
      return false;
    }

    return rewards && rewards.length > 0;
  } catch (error) {
    console.error("Error checking internal rewards:", error);
    return false;
  }
};

export interface FaucetClaimResult {
  success: boolean;
  faucetClaimId?: number;
  error?: string;
}

export const checkAndProcessFaucetClaim = async (
  walletAddress: string, 
  mission: any,
  handleRewardCallback: (mission: any) => Promise<any>
): Promise<FaucetClaimResult> => {
  try {
    // Get today's start in UTC
    const startOfDay = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), 0, 0, 0));
    
    const { data: faucetClaim, error } = await supabase
      .from('tokens_faucet_claims')
      .select('*')
      .eq('wallet_address', walletAddress)
      .eq('reward_claimed', false)
      .gte('created_at', startOfDay.toISOString())
      .limit(1);

    if (error) {
      console.error("Error checking faucet claims:", error);
      return { success: false, error: error.message };
    }

    if (faucetClaim && faucetClaim.length > 0) {
      try {
        // Call the provided callback to handle the reward
        await handleRewardCallback(mission);
        
        // Update the faucet claim as claimed
        const { error: updateError } = await supabase
          .from('tokens_faucet_claims')
          .update({ 
            reward_claimed: true,
            claimed_at: new Date().toISOString()
          })
          .eq('id', faucetClaim[0].id);
          
        if (updateError) {
          console.error("Error updating faucet claim:", updateError);
          return { success: false, error: updateError.message };
        }
        
        return { success: true, faucetClaimId: faucetClaim[0].id };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to claim mission reward";
        console.error("Error processing faucet claim:", errorMessage);
        return { success: false, error: errorMessage };
      }
    }
    
    // No faucet claim found
    return { success: false, error: "No unclaimed faucet request found" };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Error checking faucet claim";
    console.error("Error in checkAndProcessFaucetClaim:", errorMessage);
    return { success: false, error: errorMessage };
  }
};

export interface OperationSatoshiLevelCheckResult {
  success: boolean;
  userLevel?: number;
  requiredLevel?: number;
  error?: string;
}

export const checkOperationSatoshiLevelRequirement = async (
  uid: string,
  mission: any
): Promise<OperationSatoshiLevelCheckResult> => {
  try {
    // Extract required level from mission brief (e.g., "Reach Level 5" → 5)
    const levelMatch = mission.brief?.match(/level\s+(\d+)/i);
    if (!levelMatch) {
      return { 
        success: false, 
        error: "Could not extract level requirement from mission brief" 
      };
    }
    
    const requiredLevel = parseInt(levelMatch[1], 10);
    
    // Query the game_operation_satoshi_user_data table for user's current level
    const { data: userData, error } = await supabase
      .from('game_operation_satoshi_user_data')
      .select('player_level')
      .eq('uid', uid)
      .limit(1);

    if (error) {
      console.error("Error checking Operation Satoshi user data:", error);
      return { success: false, error: error.message };
    }

    if (!userData || userData.length === 0) {
      return { 
        success: false, 
        error: "User data not found in Operation Satoshi game",
        requiredLevel 
      };
    }

    const userLevel = userData[0].player_level || 0;
    const hasReachedLevel = userLevel >= requiredLevel;

    return {
      success: hasReachedLevel,
      userLevel,
      requiredLevel,
      error: hasReachedLevel ? undefined : `User level ${userLevel} is below required level ${requiredLevel}`
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Error checking Operation Satoshi level requirement";
    console.error("Error in checkOperationSatoshiLevelRequirement:", errorMessage);
    return { success: false, error: errorMessage };
  }
}; 

// utils/supabaseFunctions.ts
import { v4 as uuidv4 } from 'uuid';
import { supabase as supabaseClient } from '@/supabase/client';

// Get all reports for a user
export const getUserReports = async (uid: string) => {
  try {
    // Get all reports for the user
    const { data: reports, error } = await supabaseClient
      .from('user_reports')
      .select('*')
      .eq('uid', uid)
      .order('updated_at', { ascending: false })
      .order('datetime_current', { ascending: false });

    if (error) {
      throw error;
    }

    // For each report, get its comments
    const reportsWithComments = await Promise.all(
      reports.map(async (report) => {
        const { data: comments, error: commentsError } = await supabaseClient
          .from('user_report_comments')
          .select('*')
          .eq('report_id', report.report_id)
          .order('created_at', { ascending: true });

        if (commentsError) {
          console.error("Error fetching comments for report:", commentsError);
          return { ...report, comments: [] };
        }

        return { ...report, comments };
      })
    );

    return reportsWithComments;
  } catch (error) {
    console.error("Error fetching user reports:", error);
    throw error;
  }
};

// Remove a user report
export const removeUserReport = async (report_id: string) => {
  try {
    // Delete report comments first to maintain referential integrity
    const { error: commentsError } = await supabaseClient
      .from('user_report_comments')
      .delete()
      .eq('report_id', report_id);

    if (commentsError) {
      console.error("Error deleting report comments:", commentsError);
      return false;
    }

    // Then delete the report
    const { error } = await supabaseClient
      .from('user_reports')
      .delete()
      .eq('report_id', report_id);

    if (error) {
      console.error("Error deleting report:", error);
      return false;
    }

    console.log("Report successfully deleted!");
    return true;
  } catch (error) {
    console.error("Error deleting report:", error);
    return false;
  }
};

// Update a user report
export const updateUserReport = async (report_id: string, data: any) => {
  try {
    // Format timestamps if present
    if (data.date_time_issue) {
      // Ensure date_time_issue is a timestamp with timezone
      if (typeof data.date_time_issue === 'number') {
        // Convert timestamp to ISO string if it's a number
        data.date_time_issue = new Date(data.date_time_issue).toISOString();
      }
    }

    if (data.datetime_current) {
      // Ensure datetime_current is a timestamp with timezone
      if (typeof data.datetime_current === 'number') {
        // Convert timestamp to ISO string if it's a number
        data.datetime_current = new Date(data.datetime_current).toISOString();
      }
    }

    // Update the updated_at field
    data.updated_at = new Date().toISOString();

    const { error } = await supabaseClient
      .from('user_reports')
      .update(data)
      .eq('report_id', report_id);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error updating report:", error);
    throw error;
  }
};

// Add comment to a user report
export const updateUserReportComments = async (data: {
  uid: any;
  user_comment: any;
  created_at: any;
  report_id: any;
}) => {
  try {
    // Check if this comment already exists
    const { data: existingComment, error: queryError } = await supabaseClient
      .from('user_report_comments')
      .select('*')
      .eq('user_id', data.uid)
      .eq('user_comment', data.user_comment)
      .eq('report_id', data.report_id);

    if (queryError) {
      console.error("Error querying for existing comments:", queryError);
      return false;
    }

    if (existingComment && existingComment.length > 0) {
      // Comment already exists
      return false;
    }

    // Insert the new comment
    const comment_id = uuidv4();
    const { error: insertError } = await supabaseClient
      .from('user_report_comments')
      .insert({
        comment_id,
        user_id: data.uid,
        user_comment: data.user_comment,
        created_at: data.created_at,
        report_id: data.report_id,
      });

    if (insertError) {
      console.error("Error inserting new comment:", insertError);
      return false;
    }

    // Update the report's updated_at timestamp
    const { error: updateError } = await supabaseClient
      .from('user_reports')
      .update({ updated_at: new Date().toISOString() })
      .eq('report_id', data.report_id);

    if (updateError) {
      console.error("Error updating report timestamp:", updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating user report comments:", error);
    return false;
  }
};

// Check and move screenshots from user reports
export const checkScreenshotOnUserReportAndMoveItToScreenshots = async (reports: any[]) => {
  try {
    if (!reports || reports.length === 0) {
      return reports;
    }

    const updatedReports = [...reports];

    for (const report of updatedReports) {
      if (report.screenshot) {
        // If there is a value in the screenshot field, move it to the screenshots array
        let screenshots = report.screenshots || [];

        // Only add the screenshot if it's not already in the array
        if (!screenshots.includes(report.screenshot)) {
          screenshots.push(report.screenshot);
        }

        // Remove duplicates
        screenshots = Array.from(new Set(screenshots));

        // Update the report with new screenshots
        await supabaseClient
          .from('user_reports')
          .update({ screenshots })
          .eq('report_id', report.report_id);

        // Update the report object
        report.screenshots = screenshots;
      }
    }

    return updatedReports;
  } catch (error) {
    console.error("Error checking screenshots on user reports:", error);
    return reports; // Return original reports in case of error
  }
};

// Normalize updated_at field
export const normalizeUpdatedAtField = async (reports: any[]) => {
  try {
    if (!reports || reports.length === 0) {
      return reports;
    }

    const updatedReports = [...reports];

    for (const report of updatedReports) {
      // Check if updated_at is a string that's not an ISO date
      if (typeof report.updated_at === "string" && !report.updated_at.includes('T')) {
        // Convert the string to a timestamp and update the document
        const timestamp = parseInt(report.updated_at);
        const isoDate = new Date(timestamp * 1000).toISOString();

        await supabaseClient
          .from('user_reports')
          .update({ updated_at: isoDate })
          .eq('report_id', report.report_id);

        // Update the report object
        report.updated_at = isoDate;
      }
    }

    return updatedReports;
  } catch (error) {
    console.error("Error normalizing updated_at fields:", error);
    return reports; // Return original reports in case of error
  }
};

// Get all games
export const getGames = async () => {
  try {
    const { data: games, error } = await supabaseClient
      .from('games')
      .select('*');

    if (error) {
      throw error;
    }

    // Format games data to match the expected structure
    const gamesList = games.map(game => ({
      game_value: game.id || game.game_value,
      game_name: game.name || (game.id ? game.id.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : ''),
      ...game
    }));

    console.log("Games fetched from Supabase:", gamesList);
    return gamesList;
  } catch (error) {
    console.error("Error fetching games from Supabase:", error);
    throw error;
  }
};

// Add a new user report
export const addUserReport = async (reportData: any) => {
  try {
    // Format date fields
    if (reportData.date_time_issue && typeof reportData.date_time_issue === 'number') {
      reportData.date_time_issue = new Date(reportData.date_time_issue).toISOString();
    }
    
    if (reportData.datetime_current && typeof reportData.datetime_current === 'number') {
      reportData.datetime_current = new Date(reportData.datetime_current).toISOString();
    }

    // Set created_at and updated_at
    reportData.created_at = new Date().toISOString();
    reportData.updated_at = new Date().toISOString();

    const { data, error } = await supabaseClient
      .from('user_reports')
      .insert([reportData])
      .select();

    if (error) {
      console.error("Error adding report:", error);
      throw error;
    }

    return data ? data[0] : null;
  } catch (error) {
    console.error("Error adding report:", error);
    throw error;
  }
};

// Upload report screenshots to Supabase storage
export const uploadReportScreenshots = async (
  reportId: string,
  screenshots: (string | File)[]
) => {
  try {
    if (!screenshots || screenshots.length === 0) {
      return [];
    }

    // Process each screenshot
    const urls = await Promise.all(
      screenshots.map(async (screenshot, index) => {
        // If the screenshot is already a URL, return it as is
        if (typeof screenshot === "string") {
          return screenshot;
        }

        // For File objects, upload to Supabase Storage
        const file = screenshot as File;
        const fileExt = file.name.split('.').pop();
        const fileName = `${reportId}_${index}_${new Date().getTime()}.${fileExt}`;
        const filePath = `reports/${reportId}/${fileName}`;

        // Upload the file to the 'screenshots' bucket (which should already exist)
        // instead of trying to create a new 'reports' bucket
        const { data, error } = await supabaseClient.storage
          .from('screenshots')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error("Error uploading screenshot:", error);
          throw error;
        }

        // Get the public URL for the uploaded file
        const { data: publicURL } = supabaseClient.storage
          .from('screenshots')
          .getPublicUrl(filePath);

        return publicURL.publicUrl;
      })
    );

    return urls;
  } catch (error) {
    console.error("Error uploading screenshots:", error);
    throw error;
  }
};

// Upload a single report screenshot to Supabase storage
export const uploadReportScreenshot = async (
  reportId: string,
  screenshot: any
) => {
  try {
    // If the screenshot is already a URL, return it as is
    if (typeof screenshot === "string") {
      return screenshot;
    }

    // For File objects, upload to Supabase Storage
    return uploadReportScreenshots(reportId, [screenshot]).then(urls => urls[0]);
  } catch (error) {
    console.error("Error uploading screenshot:", error);
    throw error;
  }
}; 